/**
 * API errors (§7.4).
 *
 * `{ code, message, details? }` with stable machine codes the UI branches on. Every
 * `message` is a sentence the user needs, shown as-is — not a toast saying "Error".
 */

import { z } from 'zod';

export const apiErrorCodeSchema = z.enum([
  // audit lifecycle
  'AUDIT_CLOSED',
  'AUDIT_NOT_IN_PROGRESS',
  'STALE_VERSION',
  'SUBMIT_BLOCKED',
  // item write rules — these are the domain's WriteViolationCodes
  'EVIDENCE_REQUIRED',
  'GAP_NOTE_REQUIRED',
  'NA_NOT_CONTEXTUAL',
  'NA_JUSTIFICATION_REQUIRED',
  'RECORDED_VALUE_REQUIRED',
  'WAIVER_NOT_APPROVED',
  // authorisation
  'NOT_PROJECT_MEMBER',
  'INSUFFICIENT_ROLE',
  'NOT_SIGNATORY',
  'SAME_PERSON_CANNOT_SIGN_TWICE',
  // integrations
  'GITLAB_UNAUTHORISED',
  'GITLAB_RATE_LIMITED',
  'GITLAB_NOT_FOUND',
  // generic
  'VALIDATION_FAILED',
  'NOT_FOUND',
  'CONFLICT',
]);

export type ApiErrorCode = z.infer<typeof apiErrorCodeSchema>;

export const apiErrorSchema = z.object({
  code: apiErrorCodeSchema,
  /** Human-readable, and shown to the user unchanged. */
  message: z.string().min(1),
  details: z.record(z.unknown()).optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

/** A stale write returns the current value so the UI can show both (ADR 0005). */
export const staleVersionDetailsSchema = z.object({
  currentVersion: z.number().int().nonnegative(),
  changedByName: z.string(),
  changedAt: z.string(),
});

/**
 * Which HTTP status each code travels on. Kept here rather than in the API so the
 * client can assert the pairing in a contract test.
 */
export const ERROR_STATUS: Record<ApiErrorCode, number> = {
  AUDIT_CLOSED: 409,
  AUDIT_NOT_IN_PROGRESS: 409,
  STALE_VERSION: 409,
  SUBMIT_BLOCKED: 422,
  EVIDENCE_REQUIRED: 422,
  GAP_NOTE_REQUIRED: 422,
  NA_NOT_CONTEXTUAL: 422,
  NA_JUSTIFICATION_REQUIRED: 422,
  RECORDED_VALUE_REQUIRED: 422,
  WAIVER_NOT_APPROVED: 422,
  NOT_PROJECT_MEMBER: 403,
  INSUFFICIENT_ROLE: 403,
  NOT_SIGNATORY: 403,
  SAME_PERSON_CANNOT_SIGN_TWICE: 403,
  GITLAB_UNAUTHORISED: 502,
  GITLAB_RATE_LIMITED: 503,
  GITLAB_NOT_FOUND: 404,
  VALIDATION_FAILED: 400,
  NOT_FOUND: 404,
  CONFLICT: 409,
};
